/** Structural input shared by DB-pack and browser deterministic composition. */
export interface RankedFrameworkSignal {
 frameworkId:string; signalId:string; label:string; score:number;
 matchedEvidenceHints:string[]; matchedPositiveIndicators:string[]; matchedContextTags:string[]; reflectionPrompt:string;
}

export type FrameworkReflectionConfidence = "low" | "medium";

export type FrameworkReflectionBlock = {
  frameworkId: string;
  topSignalLabel: string;
  reflectionQuestion: string;
  supportingEvidence: string[];
  confidence: FrameworkReflectionConfidence;
};

export type AssembleFrameworkReflectionBlocksInput = {
  rankedSignals: RankedFrameworkSignal[];
  limit?: number;
};

function uniqueMatchedEvidence(signal: RankedFrameworkSignal): string[] {
  const seen = new Set<string>();
  const evidence: string[] = [];

  for (const value of [
    ...signal.matchedEvidenceHints,
    ...signal.matchedPositiveIndicators,
    ...signal.matchedContextTags,
  ]) {
    const key = value.toLowerCase().trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    evidence.push(value);
  }

  return evidence;
}

function matchedCategoryCount(signal: RankedFrameworkSignal): number {
  return [
    signal.matchedEvidenceHints.length > 0,
    signal.matchedPositiveIndicators.length > 0,
    signal.matchedContextTags.length > 0,
  ].filter(Boolean).length;
}

function calculateConfidence(signal: RankedFrameworkSignal): FrameworkReflectionConfidence {
  return signal.score >= 3 && matchedCategoryCount(signal) > 1 ? "medium" : "low";
}

export function assembleFrameworkReflectionBlocks(
  input: AssembleFrameworkReflectionBlocksInput,
): FrameworkReflectionBlock[] {
  const limit = input.limit ?? 2;

  return input.rankedSignals
    .filter((signal) => signal.score > 0)
    .slice(0, limit)
    .map((signal) => ({
      frameworkId: signal.frameworkId,
      topSignalLabel: signal.label,
      reflectionQuestion: signal.reflectionPrompt,
      supportingEvidence: uniqueMatchedEvidence(signal),
      confidence: calculateConfidence(signal),
    }));
}
